<template>
  <template
    v-if="
      isDealsIterationEnabled &&
      props.market.pricingStrategyInformation?.dealInformation
    "
  >
    <!-- Deal opportunity -->
    <PriceDisplayCell
      v-if="shouldDisplayUnlockDealButton(props.market)"
      :additionalInfo="
        i18n(translations.saveUpToCommissionRateLabel, {
          percentage:
            props.market.pricingStrategyInformation?.dealInformation
              ?.maximumCommissionDecreasePercentage,
        })
      "
      currentState="pending"
      :formattedPrice="
        i18n(translations.orLessLabel, {
          price: i18n.price(
            props.market.pricingStrategyInformation?.dealInformation?.dealPrice
              .amount,
            {
              currency: props.market.pricingStrategyInformation?.dealInformation
                ?.dealPrice?.currency as Currency,
            },
          ),
        })
      "
      :tagContent="i18n(translations.dealOpportunityLabel)"
      type="deal"
    />

    <!-- Deal opportunity is available and being used -->
    <PriceDisplayCell
      v-if="doesMarketHaveTheDeal(props.market)"
      :additionalInfo="
        i18n(translations.commissionRateIsUpToLessLabel, {
          percentage:
            props.market.pricingStrategyInformation?.dealInformation
              ?.maximumCommissionDecreasePercentage,
        })
      "
      currentState="success"
      :formattedPrice="
        i18n(translations.orLessLabel, {
          price: i18n.price(
            props.market.pricingStrategyInformation?.dealInformation?.dealPrice
              .amount,
            {
              currency: props.market.pricingStrategyInformation?.dealInformation
                ?.dealPrice?.currency as Currency,
            },
          ),
        })
      "
      :tagContent="i18n(translations.gotTheDealLabel)"
      type="deal"
    />

    <!-- Deal opportunity available but deal price higher than current backbox price and market doesn't have the backbox -->
    <PriceDisplayCell
      v-else-if="
        !shouldDisplayUnlockDealButton(props.market) &&
        !doesMarketHaveTheDeal(props.market)
      "
      :additionalInfo="i18n(translations.almostThereLabel)"
      currentState="default"
      :formattedPrice="
        i18n(translations.orLessLabel, {
          price: i18n.price(
            props.market.pricingStrategyInformation?.dealInformation?.dealPrice
              .amount,
            {
              currency: props.market.pricingStrategyInformation?.dealInformation
                ?.dealPrice?.currency as Currency,
            },
          ),
        })
      "
      :tagContent="i18n(translations.dealOpportunityLabel)"
      type="deal"
    />
  </template>

  <!-- Sales maximizer is being utilized and winning the backbox -->
  <PriceDisplayCell
    v-if="isMarketUsingSalesMaximizerPrice"
    :additionalInfo="
      i18n(translations.estimatedSales, {
        units: market.pricingStrategy?.estimatedDemand,
      })
    "
    currentState="success"
    :tagContent="i18n(translations.salesMaximizerTagLabelSuccess)"
    type="salesMaximizerPrice"
  />

  <!-- Sales maximizer is available but not being utilized -->
  <PriceDisplayCell
    v-else-if="
      props.market.pricingStrategy?.salesMaximizerPrice &&
      shouldDisplaySalesMaximizerButton(props.market)
    "
    :additionalInfo="
      i18n(translations.estimatedSales, {
        units: market.pricingStrategy?.estimatedDemand,
      })
    "
    currentState="default"
    :formattedPrice="
      i18n.price(props.market.pricingStrategy?.salesMaximizerPrice.amount, {
        currency: props.market.pricingStrategy?.salesMaximizerPrice
          ?.currency as Currency,
      })
    "
    :tagContent="i18n(translations.salesMaximizerTagLabelAvailable)"
    type="salesMaximizerPrice"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Currency, MarketCountryCode } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type { ListingV2, MarketListing } from '~/api/listings/types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import PriceDisplayCell from '../PriceDisplayCell/PriceDisplayCell.vue'

import translations from './PricingStrategyCell.translations'
import {
  doesMarketHaveTheDeal,
  isUsingSalesMaximizerPrice,
  shouldDisplaySalesMaximizerButton,
  shouldDisplayUnlockDealButton,
} from './pricingStrategy.helpers'

const i18n = useI18n()

const props = defineProps<{
  market: MarketListing & {
    countryCode: MarketCountryCode
    backBoxIndicator: 'withoutBackbox' | 'withBackbox' | 'offline'
  }
  listing: ListingV2
}>()

const { isFeatureEnabled } = useBusinessFeaturesStore()
const isDealsIterationEnabled = isFeatureEnabled('isDealsListingsPageEnabled')

const isMarketUsingSalesMaximizerPrice = computed(() => {
  return isUsingSalesMaximizerPrice(props.market)
})
</script>
